import { useDispatch } from 'react-redux';
import { useTypedSelector } from '@cube3/state/src/redux/components/Hooks/useTypedSelector';
import {
  actionCreators,
  ColumnConfig,
  OrderBy,
  OrderDirection,
  uiSelectors
} from '@cube3/state/src/redux/ducks/ui';
import { useCallback } from 'react';
import { ContentTreeContext, SortableListType } from '@cube3/state/types';
import { Contract } from '@cube3/common/model/schema/resources/contract';

const GRID = 'GRID';
const LIST = 'LIST';

export const contentTreeViewTypes = {
  GRID,
  LIST
};

type ContentTreeViewType = keyof typeof contentTreeViewTypes;
interface UISettings extends ColumnConfig {
  infoPane: boolean;
  fullscreenActive: boolean;
  fullscreenRequested: boolean;
  view: ContentTreeViewType;
  maxGridItems: number;
  maxColumns: number;
  /** the container to list all the items */
  containerWidth: number;
  /** the maxt amount of columns in a resizable table */
  maxListColumns: number;
  sortBy: OrderBy;
  sortDirection: OrderDirection;
  selectedContract: Contract;
  setGridView(): void;
  setListView(): void;
  setSortDirection(value: {
    listType: ContentTreeContext;
    field: OrderDirection;
    fieldParent: SortableListType;
  }): void;
  setSortType(value: { field: OrderBy; listType: ContentTreeContext }): void;
  setColumnWidth: (index: number, width: number) => void;
  toggleColumn: (field: SortableListType) => void;
  setContainerWidth: (width: number) => void;
  setColumns: (value: {
    columns: SortableListType[];
    widths: number[];
  }) => void;
  setMaxListColumns: (val: number) => void;
  moveColumn: (field: SortableListType, dir: 'right' | 'left') => void;
  selectContract: (contract) => void;
}

export const useUiSettings = (
  variant: ContentTreeContext = 'library'
): UISettings => {
  const dispatch = useDispatch();

  const { orderBy, orderDirection, activeColumns, columnsWidth } =
    useTypedSelector(uiSelectors.getSortingState(variant));
  const {
    gridView,
    containerWidth,
    infoPaneToggled: infoPane,
    fullscreen: fullscreenActive,
    fullscreenRequested,
    selectedContract
  } = useTypedSelector(uiSelectors.getUiState());
  const maxGridItems = useTypedSelector(uiSelectors.getMaxGridColumns());
  const maxColumns = useTypedSelector(uiSelectors.getMaxColumns());
  const maxListColumns = useTypedSelector(uiSelectors.getMaxListColumns());

  const setGridView = useCallback(
    () => dispatch(actionCreators.toggleGridView()),
    [dispatch]
  );
  const setListView = useCallback(
    () => dispatch(actionCreators.toggleListView()),
    [dispatch]
  );
  const setSortType = useCallback(
    (value) => dispatch(actionCreators.setSortType(value)),
    [dispatch]
  );
  const setSortDirection = useCallback(
    (value) => dispatch(actionCreators.setOrderDirection(value)),
    [dispatch]
  );

  const setContainerWidth = useCallback(
    (value) => dispatch(actionCreators.setContainerWidth(value)),
    [dispatch]
  );

  const setColumnWidth = useCallback(
    (index: number, width: number | string) =>
      dispatch(actionCreators.setColumnWidth(variant, index, width)),
    [dispatch]
  );
  const toggleColumn = useCallback(
    (field) => dispatch(actionCreators.toggleColumn(variant, field)),
    [dispatch]
  );

  const setColumns = useCallback(
    (value) => dispatch(actionCreators.setColumns(variant, value)),
    [dispatch]
  );

  const setMaxListColumns = useCallback(
    (value) => dispatch(actionCreators.setMaxListColumns(value)),
    [dispatch]
  );
  /** Move column to left or right */
  const moveColumn = useCallback(
    (field, dir) => dispatch(actionCreators.moveColumn(variant, field, dir)),
    [dispatch]
  );

  const selectContract = useCallback(
    (value) => {
      dispatch(actionCreators.selectContract(value));
    },
    [dispatch]
  );

  return {
    infoPane,
    fullscreenActive,
    fullscreenRequested,
    view: gridView ? GRID : LIST,
    maxGridItems,
    maxColumns,
    sortBy: orderBy,
    sortDirection: orderDirection,
    activeColumns,
    columnsWidth,
    containerWidth,
    maxListColumns,
    selectedContract,
    setGridView,
    setListView,
    setSortDirection,
    setSortType,
    setColumnWidth,
    toggleColumn,
    setContainerWidth,
    setColumns,
    setMaxListColumns,
    moveColumn,
    selectContract
  };
};
